import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useDeviceType } from "@overrides/utilities";
import {
  HotelsRawEventName,
  ShopMode,
  useEmitCoreHotelsRawEvent,
} from "@tenant/hotels";
import { HotelsAvailabilitySelectors } from "../../../store";

export const useAvailabilityPageTracking = (params: {
  shopMode: ShopMode;
  isReady: boolean;
  skipAvailabilityLoadedEvent?: boolean;
}) => {
  const { shopMode, isReady, skipAvailabilityLoadedEvent = false } = params;
  const { isDesktopAndUp } = useDeviceType();
  const searchId = useSelector(HotelsAvailabilitySelectors.getSearchId);
  const hotels = useSelector(HotelsAvailabilitySelectors.getUnsortedHotels);
  const prevSearchIdRef = useRef<string | null>();
  const pageInfo = useRef({ pageIndex: 0, previousNumHotels: 0 });

  // hotel_loaded_list_page event
  const emitHotelAvailabilityPageLoadedEvent = useEmitCoreHotelsRawEvent(
    HotelsRawEventName.HotelAvailabilityPageLoaded
  );

  // hotel_updated_list_page event
  const emitHotelUpdatedListPage = useEmitCoreHotelsRawEvent(
    HotelsRawEventName.HotelUpdatedListPage
  );

  const emitMapVisibleEvent = useEmitCoreHotelsRawEvent(
    HotelsRawEventName.HotelAvailabilityPageMapViewed
  );

  const emitListVisibleEvent = useEmitCoreHotelsRawEvent(
    HotelsRawEventName.HotelAvailabilityPageListViewed
  );

  useEffect(() => {
    if (!isReady || hotels.length === 0) {
      return;
    }
    if (prevSearchIdRef.current !== searchId) {
      prevSearchIdRef.current = searchId;
      const pageIndex = 0;
      const previousNumHotels = hotels.length;
      if (!skipAvailabilityLoadedEvent) {
        emitHotelAvailabilityPageLoadedEvent({
          properties: {
            pageIndex,
            pagePropertyCount: previousNumHotels,
          },
        });
      }
      pageInfo.current = { pageIndex, previousNumHotels };
    } else {
      const pageIndex = pageInfo.current.pageIndex + 1;
      const pagePropertyCount =
        hotels.length - pageInfo.current.previousNumHotels;
      emitHotelUpdatedListPage({
        properties: {
          pageIndex,
          pagePropertyCount,
        },
      });
      pageInfo.current = { pageIndex, previousNumHotels: hotels.length };
    }
  }, [
    skipAvailabilityLoadedEvent,
    emitHotelAvailabilityPageLoadedEvent,
    emitHotelUpdatedListPage,
    isReady,
    searchId,
    hotels.length,
  ]);

  useEffect(() => {
    if (!isReady) {
      return;
    }
    if (shopMode === ShopMode.MAP || isDesktopAndUp) {
      // Emit the tracking event always when it is desktop, or when it is mobile and the mode is map
      emitMapVisibleEvent();
    } else if (shopMode === ShopMode.LIST && !isDesktopAndUp) {
      // Emit the tracking event only when it is mobile and the mode is list
      emitListVisibleEvent();
    }
  }, [
    emitListVisibleEvent,
    emitMapVisibleEvent,
    isDesktopAndUp,
    isReady,
    shopMode,
  ]);
};
